import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  CardMedia,
  CircularProgress,
  Container,
  Hidden,
  Divider,
  IconButton,
  MenuItem,
  styled,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEmbeddedExitConfirm } from "../../../hooks/useEmbeddedExitConfirm";
import EmbeddedExitConfirmDialog from "../../modals/EmbeddedExitConfirmDialog";
import CustomChip from "../../utilities/CustomChip";
import {
  BCPBookmark,
  BCPCalendarDay,
  BCPCircleInfo,
  BCPCircleStar,
  BCPClock,
  BCPEllipsis,
  BCPEllipsisVertical,
  BCPLayerGroup,
  BCPLinkSimple,
  BCPLocationDot,
  BCPSparkles,
  BCPTicket,
  BCPUserGroup,
} from "../../icons/bcp/BCPIcons";
import { tooltipClasses } from "@mui/material/Tooltip";
import PiratePeteIcon from "../../icons/PiratePeteIcon";

import { postNotification } from "../../utilities/Notifications";
import ModalLoadingFull from "../../modals/ModalLoadingFull";
import StyledMenu from "../StyledMenu";
import { useConfirm } from "material-ui-confirm";
import moment from "moment";
import {
  deleteData,
  getData,
  postData,
} from "../../utilities/UtilityFunctions";
import { getEnvironmentVariable } from "../../../global/environmentVariables";
import OrganizationCardSmall from "../OrganizationCardSmall";
import UserCardSmall from "../UserCardSmall";

const IS_GW = getEnvironmentVariable("COMPANY_FLAG") === "gw";

export default function EventCardDefault(props) {
  const {
    organize,
    event,
    showStatus,
    authContext,
    deleteEvent,
    preventImageFetch,
  } = props;

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const {
    dialogOpen,
    handleConfirm,
    handleCancel,
    createClickHandler,
    brandName,
    storagePrefix,
  } = useEmbeddedExitConfirm();

  const [photo, setPhoto] = useState(null);
  const [toolTips, setTooltips] = useState({
    copy: "Copy Event Link",
    registration: "Event Registration",
    bookmark:
      event?.userFollowing || event?.following
        ? "Remove bookmark"
        : "Bookmark Event",
  });
  const [bookmark, setBookmark] = useState(
    event?.userFollowing || event?.following || false
  );
  const [processing, setProcessing] = useState(false);
  const [showButtons, setShowButtons] = useState(false);

  const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);

  const eventDate = moment(
    event?.eventDate ? event.eventDate : event?.dates?.start
  );

  const eventStatus =
    event?.ended || event?.status?.ended
      ? "Ended"
      : event?.started || event?.status?.started
        ? "Started"
        : "Not Started";

  const eventSubType =
    getEnvironmentVariable("COMPANY_FLAG") !== "solforge" &&
    event.eventSubType &&
    event.eventSubType !== ""
      ? event.eventSubType
      : "";

  const LightTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      color: "#ffffff",
      backgroundColor: "rgba(0, 0, 0, 0.87)",
      boxShadow: theme.shadows[1],
      fontSize: 11,
    },
  }));

  const iconSx = {
    p: 1,
    borderRadius: 1,
    backgroundColor: theme.palette.background.paper,
    zIndex: 1,
    "&:hover": { backgroundColor: theme.palette.primary.main },
    "&:active": { backgroundColor: theme.palette.primary.main },
    "&.Mui-disabled": {
      background: theme.palette.text.disabled,
    },
  };

  const cardSmallSx = {
    border: "",
    backgroundColor: "inherit",
    ml: -2,
    mb: 1,
    "&:hover": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? "background.tertiary"
          : "background.secondary",
      transition: "0.2s",
    },
    width: `calc(100% + 2rem)`,
  };

  useEffect(() => {
    const check = async () => {
      setPhoto(null);
      if (event?.photoFile?.cloudFrontDomain) {
        setPhoto(event.photoFile);
      } else if (event?.photoFileId && !preventImageFetch) {
        try {
          const apiPhoto = await getData(`/files/${event.photoFileId}`);
          setPhoto(apiPhoto);
        } catch (err) {
          console.error(err);
        }
      }
    };
    if (!IS_GW) check();
    // eslint-disable-next-line
  }, [props]);

  function CardBody() {
    return (
      <CardContent sx={{ mt: -3 }}>
        {event?.gameStore?.id ? (
          <OrganizationCardSmall
            organization={event.gameStore}
            buttonSx={cardSmallSx}
            organize={organize}
          />
        ) : (
          <UserCardSmall
            user={event?.owner}
            buttonSx={cardSmallSx}
            organize={organize}
          />
        )}

        {(event.isOnlineEvent ||
          Boolean(
            event?.city ||
              event?.location?.city ||
              event?.zip ||
              event?.location?.zip ||
              event?.state ||
              event?.location?.state ||
              event?.country ||
              event?.location?.country
          )) && (
          <Typography gutterBottom>
            <Box
              sx={{
                minWidth: "35px",
                verticalAlign: "bottom",
                display: "inline-flex",
              }}
            >
              <Tooltip title="Event Location">
                <BCPLocationDot
                  style={{
                    color: theme?.palette?.text?.secondary,
                    "vertical-align": "bottom",
                  }}
                />
              </Tooltip>
            </Box>
            {event.isOnlineEvent
              ? "Online Event"
              : `${event?.city || event?.location?.city ? `${event?.city || event?.location?.city}, ` : ""}${event?.state || event?.location?.state} ${
                  event?.country || event?.location?.country
                }`.trim()}
          </Typography>
        )}

        <Typography gutterBottom>
          <Box
            sx={{
              minWidth: "35px",
              verticalAlign: "bottom",
              display: "inline-flex",
            }}
          >
            <Tooltip title="Event Date">
              <BCPCalendarDay
                style={{
                  color: theme.palette.text.secondary,
                  "vertical-align": "bottom",
                }}
              />
            </Tooltip>
          </Box>
          {eventDate.format("LL")}
        </Typography>

        <Typography gutterBottom>
          <Box
            sx={{
              minWidth: "35px",
              verticalAlign: "bottom",
              display: "inline-flex",
            }}
          >
            <Tooltip title="Event Time">
              <BCPClock
                style={{
                  color: theme.palette.text.secondary,
                  "vertical-align": "bottom",
                }}
              />
            </Tooltip>
          </Box>
          {`${eventDate.format("LT")} - ${moment(event.eventEndDate).format(
            "LT"
          )}`}
        </Typography>

        {Boolean(
          event?.pricingInfo?.amount > 0 || event?.ticketing?.price > 0
        ) && (
          <Typography gutterBottom>
            <Box
              sx={{
                minWidth: "35px",
                verticalAlign: "bottom",
                display: "inline-flex",
              }}
            >
              <Tooltip title="Ticket Price">
                <BCPTicket
                  style={{
                    color: theme?.palette?.text?.secondary,
                    "vertical-align": "bottom",
                  }}
                />
              </Tooltip>
            </Box>
            {`${
              event?.pricingInfo?.amount || event?.ticketing?.price
            } ${(event?.pricingInfo?.currency || event?.ticketing?.currency || "").toUpperCase()}`}
          </Typography>
        )}

        {!event?.hidePlayerCount &&
          !event?.flags?.hidePlayerCount &&
          (event.numTickets > 0 ||
            event.totalPlayers > 0 ||
            (event?.countLabel && event?.countString)) && (
            <Typography gutterBottom>
              <Box
                sx={{
                  minWidth: "35px",
                  verticalAlign: "bottom",
                  display: "inline-flex",
                }}
              >
                <Tooltip title="Number of Players">
                  <BCPUserGroup
                    style={{
                      color: theme?.palette?.text?.secondary,
                      "vertical-align": "bottom",
                    }}
                  />
                </Tooltip>
              </Box>
              {event?.numTickets > 0 || event?.totalPlayers > 0
                ? `${
                    event.ended || event.started
                      ? // && event.currentRound > 0
                        event.checkedInPlayers
                      : event.totalPlayers
                  }${
                    !event.started && event.numTickets > 0
                      ? ` / ${event.numTickets}`
                      : ` Registered ${event?.teamsEvent ? "Teams" : "Players"}`
                  }`
                : `${event?.countString} ${event?.countLabel}`}
            </Typography>
          )}

        {event.numberOfRounds && event.numberOfRounds > 0 ? (
          <Typography gutterBottom>
            <Box
              sx={{
                minWidth: "35px",
                verticalAlign: "bottom",
                display: "inline-flex",
              }}
            >
              <Tooltip title="Number of Rounds">
                <BCPLayerGroup
                  style={{
                    color: theme?.palette?.text?.secondary,
                    "vertical-align": "bottom",
                  }}
                />
              </Tooltip>
            </Box>
            {event.numberOfRounds} Rounds
          </Typography>
        ) : (
          <></>
        )}

        {event?.leagues?.length > 0 ? (
          <Typography gutterBottom>
            <Box
              sx={{
                minWidth: "35px",
                verticalAlign: "bottom",
                display: "inline-flex",
              }}
            >
              <Tooltip title="Circuits">
                <BCPCircleStar
                  style={{
                    color: theme.palette.text.secondary,
                    "vertical-align": "bottom",
                  }}
                />
              </Tooltip>
            </Box>
            {event.leagues.map(
              (league, index) =>
                league.name + (index < event.leagues.length - 1 ? ", " : "")
            )}
          </Typography>
        ) : (
          <></>
        )}

        {Boolean(event?.eventFormat || event?.format) && (
          <Typography gutterBottom>
            <Box
              sx={{
                minWidth: "35px",
                verticalAlign: "bottom",
                display: "inline-flex",
              }}
            >
              <Tooltip
                title={
                  event?.gameSystem?.eventFormats?.label ||
                  (event?.format?.leagueEvent
                    ? "League Event"
                    : event?.format?.doublesEvent
                      ? "Doubles Event"
                      : event?.format?.teamEvent
                        ? "Teams Event"
                        : event?.format?.boardGameEvent
                          ? "Board Game Event"
                          : "Singles Event")
                }
              >
                <BCPSparkles
                  style={{
                    color: theme?.palette?.text?.secondary,
                    verticalAlign: "bottom",
                  }}
                />
              </Tooltip>
            </Box>
            {event?.gameSystem?.eventFormats?.label ||
              (event?.format?.leagueEvent
                ? "League Event"
                : event?.format?.doublesEvent
                  ? "Doubles Event"
                  : event?.format?.teamEvent
                    ? "Teams Event"
                    : event?.format?.boardGameEvent
                      ? "Board Game Event"
                      : "Singles Event")}
          </Typography>
        )}

        {Boolean(showStatus) && (
          <Typography gutterBottom>
            <Box
              sx={{
                minWidth: "35px",
                verticalAlign: "bottom",
                display: "inline-flex",
              }}
            >
              <Tooltip title="Event Status">
                <BCPCircleInfo
                  style={{
                    color: theme?.palette?.text?.secondary,
                    "vertical-align": "bottom",
                  }}
                />
              </Tooltip>
            </Box>
            Event {eventStatus}
          </Typography>
        )}
      </CardContent>
    );
  }

  return (
    <>
      <Card
        sx={{ height: "100%" }}
        onMouseEnter={() => {
          if (isMdUp) {
            setShowButtons(true);
          }
        }}
        onMouseLeave={() => {
          if (isMdUp) {
            setShowButtons(false);
          }
        }}
      >
        {/* Hover buttons */}

        {!IS_GW && (
          <Container
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              p: 1,
              gap: 1,
              mb: -7,
              mr: [0, -2],
              zIndex: 1000,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                visibility: [showButtons ? "visible" : "hidden"],
              }}
            >
              {event?.usingOnlineReg && !event.ended && (
                <LightTooltip title={toolTips.registration}>
                  <IconButton
                    aria-label="registration"
                    sx={iconSx}
                    href={`/event/${event?.id}?active_tab=overview&register=true`}
                  >
                    <BCPTicket />
                  </IconButton>
                </LightTooltip>
              )}

              <LightTooltip
                title={toolTips.copy}
                PopperProps={{
                  disablePortal: true,
                  popperOptions: {
                    positionFixed: true,
                    modifiers: {
                      preventOverflow: {
                        enabled: true,
                        boundariesElement: "window",
                      },
                    },
                  },
                }}
              >
                <IconButton
                  aria-label="event link"
                  sx={iconSx}
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `${window.location.origin}/event/${event?.id}`
                    );
                    setTooltips({
                      ...toolTips,
                      copy: "Copied Link!",
                    });
                    setTimeout(() => {
                      setTooltips({
                        ...toolTips,
                        copy: "Copy Event Link",
                      });
                    }, 2000);
                  }}
                >
                  <BCPLinkSimple />
                </IconButton>
              </LightTooltip>

              {authContext?.authState?.identity && (
                <LightTooltip
                  title={toolTips.bookmark}
                  PopperProps={{
                    disablePortal: true,
                    popperOptions: {
                      positionFixed: true,
                      modifiers: {
                        preventOverflow: {
                          enabled: true,
                          boundariesElement: "window",
                        },
                      },
                    },
                  }}
                >
                  <IconButton
                    aria-label="bookmark"
                    sx={iconSx}
                    onClick={async () => {
                      try {
                        setProcessing(true);
                        const body = bookmark ? { unfollow: true } : {};

                        await postData(`/events/${event?.id}/follow`, {
                          body: body,
                        });

                        setTooltips({
                          ...toolTips,
                          bookmark: bookmark
                            ? "Removed Bookmark!"
                            : "Event Bookmarked!",
                        });
                        setTimeout(() => {
                          setTooltips({
                            ...toolTips,
                            bookmark: bookmark
                              ? "Bookmark Event"
                              : "Remove Bookmark",
                          });
                        }, 2000);

                        setBookmark(!bookmark);
                      } catch (err) {
                        postNotification("error", err);
                      } finally {
                        setProcessing(false);
                      }
                    }}
                    disabled={processing}
                  >
                    {processing ? (
                      <CircularProgress size={"22px"} />
                    ) : (
                      <BCPBookmark outlined={bookmark} />
                    )}
                  </IconButton>
                </LightTooltip>
              )}
            </Box>

            <Hidden mdUp>
              <IconButton
                aria-label="more"
                sx={iconSx}
                onClick={() => {
                  setShowButtons(!showButtons);
                }}
              >
                <BCPEllipsis />
              </IconButton>
            </Hidden>
          </Container>
        )}

        <CardActionArea
          href={organize ? `/organize/event/${event.id}` : `/event/${event.id}`}
          target="_blank"
          onClick={createClickHandler(
            organize ? `/organize/event/${event.id}` : `/event/${event.id}`,
            "_blank"
          )}
          sx={{ height: IS_GW ? "100%" : "auto" }}
        >
          {!IS_GW && (
            <CardMedia
              component="img"
              image={
                photo?.url ||
                event?.photoUrl ||
                `${getEnvironmentVariable(
                  "SYSTEM_IMAGES_CLOUDFRONT"
                )}/images/EventImage${
                  theme?.palette?.mode === "dark" ? "Dark" : ""
                }.png`
              }
              alt={event?.name}
              sx={{
                backgroundColor: "#222222",
                maxHeight: "calc((100vw - 24px) * 0.5625)",
                "@media (min-width: 600px)": {
                  maxHeight: "calc((100vw - 24px) * 0.5 * 0.5625)",
                },
                "@media (min-width: 900px)": {
                  maxHeight: "calc((100vw - 24px) * 0.3333 * 0.5625)",
                },
                "@media (min-width: 1218px)": {
                  maxHeight: "215px",
                },
              }}
            />
          )}

          <CardHeader
            title={
              <>
                {getEnvironmentVariable("COMPANY_FLAG") === "solforge" &&
                  event.eventSubType && (
                    <CustomChip
                      label={`${event.eventSubType} Event`}
                      color={event.eventSubType.toLowerCase()}
                    />
                  )}

                {(!getEnvironmentVariable("SINGLE_GAME_SYSTEM") ||
                  eventSubType) && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontWeight: IS_GW ? "600" : "400",
                      fontSize: "16px",
                      lineHeight: "1.334",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      textTransform: IS_GW ? "uppercase" : "none",
                    }}
                  >
                    {IS_GW && <PiratePeteIcon fontSize="medium" />}
                    {!getEnvironmentVariable("SINGLE_GAME_SYSTEM")
                      ? event.gameSystem?.name || event?.gameSystemName
                      : ""}
                    {eventSubType &&
                    event.gameSystem &&
                    !getEnvironmentVariable("SINGLE_GAME_SYSTEM")
                      ? ` | ${eventSubType}`
                      : eventSubType || ""}
                  </Typography>
                )}

                {IS_GW && (
                  <Divider
                    sx={{
                      mt: 1,
                      mb: 1,
                      borderColor:
                        // Will likely remove/update based on Casey's feedback
                        // 40K
                        event?.gameSystem?.code === 1 || event?.gameType === 1
                          ? "#2b658e"
                          : // Age of Sigmar
                            event?.gameSystem?.code === 4 ||
                              event?.gameType === 4
                            ? "#d3ba6e"
                            : // Blood Bowl
                              event?.gameSystem?.code === 8 ||
                                event?.gameType === 8
                              ? "#a50a0e"
                              : // UnderWorlds
                                event?.gameSystem?.code === 9 ||
                                  event?.gameType === 9
                                ? "#a67c00"
                                : // 30K
                                  event?.gameSystem?.code === 15 ||
                                    event?.gameType === 15
                                  ? "#99bfa4"
                                  : // Kill Team
                                    event?.gameSystem?.code === 26 ||
                                      event?.gameType === 26
                                    ? "#2b658e"
                                    : "link.main",
                      borderWidth: "3px",
                      width: "150%",
                      ml: -3,
                    }}
                  />
                )}

                <Typography
                  gutterBottom
                  sx={{
                    m: 0,
                    fontWeight: "700",
                    fontSize: "20px",
                    lineHeight: "1.334",
                  }}
                >
                  {event?.name}
                </Typography>

                {(!getEnvironmentVariable("SINGLE_GAME_SYSTEM") ||
                  eventSubType) && (
                  <Typography
                    sx={{
                      fontWeight: "400",
                      fontSize: "18px",
                      lineHeight: "1.334",
                    }}
                  >
                    {eventSubType &&
                    event.gameSystem &&
                    !getEnvironmentVariable("SINGLE_GAME_SYSTEM")
                      ? ` | ${eventSubType}`
                      : eventSubType || ""}
                  </Typography>
                )}
              </>
            }
            action={
              organize ? (
                <IconButton
                  onClick={(e) => {
                    e.preventDefault();
                    setOptionsAnchorEl(e.currentTarget);
                    //
                  }}
                  aria-label={`More options for ${event?.name || "event"}`}
                >
                  <BCPEllipsisVertical />
                </IconButton>
              ) : null
            }
          />

          {IS_GW && <CardBody />}
        </CardActionArea>

        {!IS_GW && <CardBody />}
      </Card>
      {organize && (
        <>
          <OptionsMenu
            anchorEl={optionsAnchorEl}
            setAnchorEl={setOptionsAnchorEl}
            event={event}
            authContext={authContext}
            deleteEvent={deleteEvent}
          />
        </>
      )}
      <EmbeddedExitConfirmDialog
        open={dialogOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        brandName={brandName}
        storagePrefix={storagePrefix}
      />
    </>
  );
}

function OptionsMenu(props) {
  const {
    anchorEl,
    setAnchorEl,
    event,
    // authContext,
    deleteEvent,
  } = props;
  const confirm = useConfirm();
  const open = Boolean(anchorEl);
  const [loading, setLoading] = useState(false);
  return (
    <>
      <StyledMenu
        id="options-menu"
        MenuListProps={{
          "aria-labelledby": "options-button",
        }}
        disableScrollLock
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          href={`/organize/event?copyId=${event?.id}`}
          component="a"
          disableRipple
        >
          Copy Event
        </MenuItem>
        <MenuItem
          onClick={async (e) => {
            setAnchorEl(null);
            try {
              await confirm({
                title: `Are you sure?`,
                description: "Are you sure you want to delete this event?",
                dialogProps: {
                  sx: {
                    zIndex: 99999,
                  },
                },
              });
            } catch (err) {
              return;
            }
            try {
              await confirm({
                title: `Are you really sure?`,
                description:
                  "Are you really sure you want to delete this event?",
                dialogProps: {
                  sx: {
                    zIndex: 99999,
                  },
                },
              });
            } catch (err) {
              return;
            }
            // end tournament
            setLoading(true);
            try {
              await deleteData(`/events/${event?.id}`);
              if (deleteEvent) {
                deleteEvent(event?.id);
              }
            } catch (err) {
              postNotification("error", err);
            }

            setLoading(false);
          }}
          disableRipple
        >
          Delete Event
        </MenuItem>
      </StyledMenu>
      {loading && <ModalLoadingFull />}
    </>
  );
}
